import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Crown, Check, Sparkles } from "lucide-react";

const TIERS = [
  {
    key: "Black Silver",
    accent: "#B8BCC4",
    price: "₹99,999",
    tagline: "The essentials, elevated.",
    perks: [
      "Priority chef booking",
      "No booking fee",
      "10% discount on chef charges",
      "Dedicated Relationship Manager",
      "Festival menu planning",
      "Birthday & anniversary reminders",
      "Emergency replacement chef within 4 hours",
      "Complimentary kitchen audit once a year",
    ],
  },
  {
    key: "Black Gold",
    accent: "#D9A94A",
    price: "₹2,49,999",
    tagline: "Everything in Silver, and a household team.",
    featured: true,
    perks: [
      "Personal chef available on priority",
      "Monthly dinner hosted by a celebrity chef",
      "Menu planning by a nutritionist",
      "Grocery procurement assistance",
      "Pantry management",
      "Kitchen inventory management",
      "Wine & cheese pairing sessions",
      "Kids meal planning",
      "Elderly diet planning",
    ],
  },
  {
    key: "Black Signature",
    accent: "#E7D8B5",
    price: "₹4,99,999",
    tagline: "Everything in Gold, and a culinary life curated.",
    perks: [
      "Personal Culinary Manager",
      "Unlimited consultation",
      "VIP event planning",
      "Chef on standby",
      "Last-minute bookings",
      "Luxury catering discounts",
      "Farm-to-table sourcing",
      "Imported ingredients procurement",
      "Luxury villa chef while travelling",
      "24×7 concierge support",
    ],
  },
];

export default function BlackMembership() {
  const [open, setOpen] = useState(false);
  const [tier, setTier] = useState("Black Gold");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const requestInvite = (t) => {
    setTier(t);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) return toast.error("Please share your name, email and phone");
    setLoading(true);
    try {
      await api.post("/membership/enquire", { ...form, tier });
      toast.success("Invitation request received. Your Relationship Manager will be in touch.");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#080706] min-h-screen" data-testid="membership-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#C9962F]/20">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(201,150,47,0.12),transparent_60%)]" />
        <div className="relative max-w-5xl mx-auto px-6 py-24 md:py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#C9962F]/40 bg-black/40 mb-8">
            <Crown size={14} className="text-[#D9A94A]" />
            <span className="small-caps text-[10px] tracking-widest text-[#E7D8B5]">Invitation Only</span>
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
            Chef-Hire <span className="font-script text-[#D9A94A]">Black</span>
          </h1>
          <p className="font-script text-2xl md:text-3xl text-[#E7D8B5] mt-3">Your Personal Culinary Concierge</p>
          <p className="text-neutral-400 text-base mt-6 max-w-2xl mx-auto">
            A private membership for households that entertain often and expect flawlessly.
            Annual membership from <span className="text-white">₹1,00,000</span> to <span className="text-white">₹5,00,000</span>.
          </p>
        </div>
      </section>

      {/* Tiers */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid gap-6 md:grid-cols-3 items-start">
          {TIERS.map((t) => (
            <div
              key={t.key}
              data-testid={`tier-${t.key.replace(/\s+/g, "-").toLowerCase()}`}
              className={`relative flex flex-col rounded-2xl border bg-[#0E0D0B] p-8 transition-transform hover:-translate-y-1 ${
                t.featured ? "border-[#C9962F]/60 shadow-[0_20px_80px_rgba(201,150,47,0.15)]" : "border-white/10"
              }`}
            >
              {t.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-[#E0B75B] to-[#B07F27] text-black">
                  <Sparkles size={11} /> <span className="small-caps text-[9px] tracking-wide">Most Chosen</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Crown size={18} style={{ color: t.accent }} />
                <h3 className="font-serif-display text-2xl text-white">{t.key}</h3>
              </div>
              <p className="text-neutral-500 text-sm mt-1">{t.tagline}</p>
              <div className="mt-6 mb-6">
                <span className="font-serif-display text-4xl text-white">{t.price}</span>
                <span className="small-caps text-[10px] text-neutral-500 ml-2">/ year</span>
              </div>
              <ul className="space-y-3 flex-1">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-neutral-300">
                    <Check size={15} className="mt-0.5 flex-shrink-0" style={{ color: t.accent }} />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => requestInvite(t.key)}
                data-testid={`request-invite-${t.key.replace(/\s+/g, "-").toLowerCase()}`}
                className={`mt-8 w-full rounded-none h-11 small-caps text-[11px] ${
                  t.featured
                    ? "bg-gradient-to-r from-[#E0B75B] to-[#B07F27] text-black hover:opacity-90"
                    : "bg-transparent border border-[#C9962F]/50 text-[#E7D8B5] hover:bg-[#C9962F]/10"
                }`}
              >
                Request Invitation
              </Button>
            </div>
          ))}
        </div>
        <p className="text-center text-neutral-600 text-xs mt-10">
          Memberships are reviewed personally. Approval typically within 3–5 working days.
        </p>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[#0F0F0F] border border-[#C9962F]/30 rounded-none p-0 overflow-hidden" data-testid="membership-dialog">
          <div className="p-8">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl text-white">Request an Invitation</DialogTitle>
              <p className="text-neutral-400 text-sm mt-1">
                Applying for <span className="text-[#E7D8B5]">{tier}</span>. Our team will reach out discreetly.
              </p>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-6">
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="membership-name"
                  className="mt-2 bg-transparent rounded-none border-white/10 focus-visible:border-[#C9962F] focus-visible:ring-0" placeholder="Your name" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="membership-email"
                  className="mt-2 bg-transparent rounded-none border-white/10 focus-visible:border-[#C9962F] focus-visible:ring-0" placeholder="you@domain.com" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="membership-phone"
                  className="mt-2 bg-transparent rounded-none border-white/10 focus-visible:border-[#C9962F] focus-visible:ring-0" placeholder="+91 98765 43210" />
              </div>
              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Anything we should know? (optional)</Label>
                <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="membership-message"
                  className="mt-2 bg-transparent rounded-none border-white/10 focus-visible:border-[#C9962F] focus-visible:ring-0" placeholder="Household size, city, preferences…" />
              </div>
              <Button type="submit" disabled={loading} data-testid="membership-submit"
                className="w-full rounded-none bg-gradient-to-r from-[#E0B75B] to-[#B07F27] text-black h-11 small-caps text-[11px] hover:opacity-90">
                {loading ? "Submitting…" : "Submit Request"}
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
